import {
  evaluateTemplateValue,
  dataLocationToPath,
} from "@ea-lab/reactive-json";

/**
 * Generic VALUE-membership toggle on an array store slot.
 *
 * Why a custom reaction (config-first escape hatch) : adding/removing one known
 * value to/from an array is NOT expressible in reactive-json YAML.
 *   - `addData` only PUSHES (no dedup, no remove).
 *   - `removeData` removes by array INDEX, not by value.
 * There is no value-aware array toggle, and the write-path system has no
 * JSONPath predicate to target "the element equal to X". So a composable
 * on/off button (e.g. "Mes tâches" adding the current uid to an assignee
 * filter, "Urgent" flipping a boolean term) cannot be built from setData alone
 * without clobbering the OTHER values already in the slot.
 *
 * This reaction fills exactly that gap, declaratively : flip `value` in the
 * array at `path`. Absent → appended ; present → removed ; everything else in
 * the array is preserved. The slot is always normalised to an array (a missing
 * / non-array slot is treated as empty), so it is safe on a freshly-loaded page.
 *
 * If `value` resolves to `null`/`undefined` the reaction is a NO-OP — this
 * guards the case where the toggled value comes from a non-blocking source not
 * yet loaded (e.g. `~~.userInfo.uid`), so the button never writes `[null]`.
 *
 * YAML usage :
 *   - what: toggleData
 *     on: click
 *     path: ~~.selectedFilters.assignee_uid.any   # array slot
 *     value: ~~.userInfo.uid                       # value to add/remove
 *
 * Comparison is loose-by-string so a numeric uid matches a string aggregation
 * key (the same TID/uid coercion the facet primitives use).
 *
 * Registered in [App.jsx](../../../App.jsx) under `customPlugins.reaction.toggleData`.
 */
export const toggleDataReaction = (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args) return;

  const gdc = globalDataContext.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext });

  const value = resolve(args.value);
  // NO-OP when the value isn't ready (e.g. a non-blocking source still loading)
  // so we never push `null`/`undefined` into the array.
  if (value === null || value === undefined || value === "") return;

  const path =
    typeof args.path === "string"
      ? dataLocationToPath({
          dataLocation: args.path,
          currentPath: templateContext?.templatePath,
          globalDataContext: gdc,
          templateContext,
        })
      : null;
  if (!path) return;

  // `dataLocationToPath` returns a `data.`-prefixed path (e.g. "data.foo.bar").
  // `updateData` strips that prefix internally, but `store.get` is raw — so we
  // strip it here to read the live slot (mirrors ReactiveJsonRoot's `at`).
  const storePath =
    path === "data" ? "" : path.startsWith("data.") ? path.slice(5) : path;
  const current = gdc.store ? gdc.store.get(storePath) : undefined;
  const list = Array.isArray(current) ? current : [];
  const k = String(value);
  const present = list.some((v) => String(v) === k);
  const next = present ? list.filter((v) => String(v) !== k) : [...list, value];
  gdc.updateData(next, path);
};
